/**
 * `mockups/client/files.html` §3 and §4 — the monthly reports and the brand
 * assets, the two columns that belong to no month and no project.
 *
 * They sit beside the seg rather than inside it: a report is not a deliverable of
 * August and a logo pack is not a deliverable of anything, so switching "by month"
 * to "by project" has no opinion about either. `library` arrives in the same call
 * as the other two views (`ClientFilesPageSchema`), so this costs no request.
 *
 * The current month's report is the exception and the five before it are the
 * routine — DESIGN.md P3, and the mockup's own shape.
 */

import type { FilesLibraryView } from '@fareast/shared'

import { DownloadLink } from './DownloadLink.js'
import { Card, CardBody, CardHead, CardNote, CardTitle } from '../../../components/portal/Card.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { FileRow } from '../../../components/portal/Files.js'
import { Push, Row, Stack } from '../../../components/portal/Layout.js'
import { B, DateText, Muted, Small } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import { extensionOf } from './fileType.js'

/** `6 PDFs`, `1 PDF` — the mockup's own note on the reports card. */
const PDFS = { one: 'PDF', other: 'PDFs' } as const

export type LibraryProps = { readonly library: FilesLibraryView }

export function Library({ library }: LibraryProps) {
  return (
    <>
      <Reports library={library} />
      <BrandAssets brand={library.brand} />
    </>
  )
}

/* ------------------------------------------------------------------ reports */

function Reports({ library }: LibraryProps) {
  const format = useFormat()
  const [newest, ...older] = library.reports

  return (
    <Card>
      <CardHead>
        <CardTitle>Monthly reports</CardTitle>
        <CardNote push>{format.qty(library.report_pdf_count, PDFS)}</CardNote>
      </CardHead>

      {newest === undefined ? (
        <CardBody>
          <Empty>Your first report is being written.</Empty>
        </CardBody>
      ) : (
        <CardBody tight>
          <FileRow
            icon="pdf"
            title={format.month(newest.period)}
            sub={reportSub(newest)}
            meta={<OpenReport report={newest} />}
          />
        </CardBody>
      )}

      {older.length === 0 ? null : (
        <CardBody>
          <Fold summary={`${format.number(older.length)} earlier reports`}>
            <Stack gap="tight">
              {older.map((report) => (
                <Row key={report.period}>
                  <B>{format.month(report.period)}</B>
                  {report.headline === '' ? null : (
                    <Small>
                      <Muted>{report.headline}</Muted>
                    </Small>
                  )}
                  <Push />
                  <OpenReport report={report} />
                </Row>
              ))}
            </Stack>
          </Fold>
        </CardBody>
      )}
    </Card>
  )
}

type Report = FilesLibraryView['reports'][number]

function OpenReport({ report }: { readonly report: Report }) {
  const format = useFormat()
  const month = format.month(report.period)
  if (report.pdf === null) {
    return (
      <Small>
        <Muted>In preparation</Muted>
      </Small>
    )
  }
  return (
    <DownloadLink id={report.pdf.id} label={`Open the ${month} report`}>
      Open
    </DownloadLink>
  )
}

/**
 * The headline, when there is one.
 *
 * "In preparation" is `OpenReport`'s, not this line's: a month with no PDF says it
 * once, where the link would have been — P7, "don't add a chip whose word is
 * already in the sentence", and the same rule applies to a sentence beside itself.
 */
function reportSub(report: Report): string | undefined {
  return report.headline === '' ? undefined : report.headline
}

/* ------------------------------------------------------------------- brand */

function BrandAssets({ brand }: { readonly brand: FilesLibraryView['brand'] }) {
  return (
    <Card>
      <CardHead>
        <CardTitle>Brand assets</CardTitle>
      </CardHead>
      {brand.length === 0 ? (
        <CardBody>
          <Empty>No brand assets yet.</Empty>
        </CardBody>
      ) : (
        <CardBody tight>
          {brand.map((asset) => (
            <FileRow
              key={asset.id}
              icon={extensionOf(asset.name)}
              title={asset.title}
              sub={
                <>
                  Added <DateText value={asset.created_at} nowrap />
                </>
              }
              meta={<DownloadLink id={asset.id} label={`Download ${asset.title}`} />}
            />
          ))}
        </CardBody>
      )}
    </Card>
  )
}
